import { useCallback } from "react";
import profileService from "../../services/api/profileService";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_CONFIGS } from "../../lib/react-query/config";

// Profile Query Keys
export const profileQueryKeys = {
  all: ["profile"],
  users: () => [...profileQueryKeys.all, "users"],
  merchants: () => [...profileQueryKeys.all, "merchants"],
  userDetail: (userId) => [...profileQueryKeys.all, "user", userId],
  updateUser: (userId) => [...profileQueryKeys.all, "updateUser", userId],
  activateUser: (userId) => [...profileQueryKeys.all, "activateUser", userId],
  deactivateUser: (userId) => [
    ...profileQueryKeys.all,
    "deactivateUser",
    userId,
  ],
  unblockUserPin: (userId) => [
    ...profileQueryKeys.all,
    "unblockUserPin",
    userId,
  ],
  createMerchantUser: () => [...profileQueryKeys.all, "createMerchantUser"],
};

// GET ALL USERS
export const useGetAllUsersQuery = () => {
  const request = useCallback(async () => {
    try {
      const response = await profileService.getAllUsers();
      return response;
    } catch (error) {
      const errorMessage = extractAxiosErrorMessage(
        error,
        "Failed to fetch users",
      );
      throw new Error(errorMessage);
    }
  }, []);

  return useQuery({
    queryKey: profileQueryKeys.users(),
    queryFn: request,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useGetAllMerchantUsersQuery = () => {
  return useQuery({
    queryKey: profileQueryKeys.merchants(),
    queryFn: profileService.getAllMerchantUsers,
    ...QUERY_CONFIGS.STATIC,
  });
};

// GET A USER BY ID
export const useGetUserByIdQuery = (userId, options = {}) => {
  const request = useCallback(async () => {
    try {
      const response = await profileService.getUserById(userId);
      return response;
    } catch (error) {
      const errorMessage = extractAxiosErrorMessage(
        error,
        "Failed to fetch user details",
      );
      throw new Error(errorMessage);
    }
  }, [userId]);

  return useQuery({
    queryKey: profileQueryKeys.userDetail(userId),
    queryFn: request,
    enabled: !!userId && options.enabled !== false,
    ...QUERY_CONFIGS.STATIC,
  });
};

export const useCreateMerchantUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userData) => profileService.createMerchantProfile(userData),
    mutationKey: profileQueryKeys.createMerchantUser(),

    onMutate: async (newUser) => {
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.merchants(),
      });

      const previousMerchants = queryClient.getQueryData(
        profileQueryKeys.merchants(),
      );

      const tempUser = {
        id: `temp-${Date.now()}`,
        ...newUser,
        accountLocked: false,
        merchantId: "pending",
        roles: [],
        avatarBase64: null,
        avatarContentType: null,
      };

      queryClient.setQueryData(profileQueryKeys.merchants(), (old = []) => [
        ...(Array.isArray(old) ? old : []),
        tempUser,
      ]);

      return { previousMerchants };
    },

    onError: (error, newUser, context) => {
      if (context?.previousMerchants) {
        queryClient.setQueryData(
          profileQueryKeys.merchants(),
          context.previousMerchants,
        );
      }
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },

    retry: false,
  });
};

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, userData }) =>
      profileService.updateUser(userId, userData),
    mutationKey: profileQueryKeys.updateUser(),
    onMutate: async ({ userId, userData }) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKeys.users() });
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.merchants(),
      });
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });

      const previousUsers = queryClient.getQueryData(profileQueryKeys.users());
      const previousMerchants = queryClient.getQueryData(
        profileQueryKeys.merchants(),
      );
      const previousUserDetail = queryClient.getQueryData(
        profileQueryKeys.userDetail(userId),
      );

      // Optimistically update the users list
      if (previousUsers) {
        queryClient.setQueryData(profileQueryKeys.users(), (old) =>
          old.map((user) =>
            user.id === userId ? { ...user, ...userData } : user,
          ),
        );
      }

      // Optimistically update the merchants list
      if (previousMerchants) {
        queryClient.setQueryData(profileQueryKeys.merchants(), (old) =>
          old.map((user) =>
            user.id === userId ? { ...user, ...userData } : user,
          ),
        );
      }

      // Optimistically update single user detail
      if (previousUserDetail) {
        queryClient.setQueryData(profileQueryKeys.userDetail(userId), {
          ...previousUserDetail,
          ...userData,
        });
      }

      return { previousUsers, previousMerchants, previousUserDetail };
    },

    // Rollback if mutation fails
    onError: (error, { userId }, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          profileQueryKeys.users(),
          context.previousUsers,
        );
      }
      if (context?.previousMerchants) {
        queryClient.setQueryData(
          profileQueryKeys.merchants(),
          context.previousMerchants,
        );
      }
      if (context?.previousUserDetail) {
        queryClient.setQueryData(
          profileQueryKeys.userDetail(userId),
          context.previousUserDetail,
        );
      }
    },

    onSuccess: (data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.users() });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.merchants() });
    },

    onSettled: (data, error, { userId }) => {
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.users() });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({ queryKey: profileQueryKeys.merchants() });
    },

    retry: false,
  });
};

export const useActivateUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId) => profileService.activateUser(userId),
    mutationKey: profileQueryKeys.activateUser(),
    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKeys.users() });
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.merchants(),
      });
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      const previousUsers = queryClient.getQueryData(profileQueryKeys.users());
      const previousMerchants = queryClient.getQueryData(
        profileQueryKeys.merchants(),
      );
      const previousUserDetail = queryClient.getQueryData(
        profileQueryKeys.userDetail(userId),
      );

      // Optimistically update users list
      if (previousUsers) {
        queryClient.setQueryData(profileQueryKeys.users(), (old) =>
          old.map((user) =>
            user.id === userId ? { ...user, accountLocked: false } : user,
          ),
        );
      }

      // Optimistically update merchants list
      if (previousMerchants) {
        queryClient.setQueryData(profileQueryKeys.merchants(), (old) =>
          old.map((user) =>
            user.id === userId ? { ...user, accountLocked: false } : user,
          ),
        );
      }

      // Optimistically update single user
      if (previousUserDetail) {
        queryClient.setQueryData(profileQueryKeys.userDetail(userId), {
          ...previousUserDetail,
          accountLocked: false,
        });
      }

      // Return rollback snapshot
      return { previousUsers, previousMerchants, previousUserDetail };
    },
    onError: (error, userId, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          profileQueryKeys.users(),
          context.previousUsers,
        );
      }
      if (context?.previousMerchants) {
        queryClient.setQueryData(
          profileQueryKeys.merchants(),
          context.previousMerchants,
        );
      }
      if (context?.previousUserDetail) {
        queryClient.setQueryData(
          profileQueryKeys.userDetail(userId),
          context.previousUserDetail,
        );
      }
    },

    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.users(),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },
    onSettled: (data, error, userId) => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.users(),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },
    retry: false,
  });
};

export const useDeactivateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId) => profileService.deactivateUser(userId),
    mutationKey: profileQueryKeys.deactivateUser(),

    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKeys.users() });
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.merchants(),
      });
      await queryClient.cancelQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });

      // Snapshot the current state
      const previousUsers = queryClient.getQueryData(profileQueryKeys.users());
      const previousMerchants = queryClient.getQueryData(
        profileQueryKeys.merchants(),
      );
      const previousUserDetail = queryClient.getQueryData(
        profileQueryKeys.userDetail(userId),
      );

      // Optimistically update users list
      if (previousUsers) {
        queryClient.setQueryData(profileQueryKeys.users(), (old) =>
          old.map((user) =>
            user.id === userId ? { ...user, accountLocked: true } : user,
          ),
        );
      }

      // Optimistically update merchants list
      if (previousMerchants) {
        queryClient.setQueryData(profileQueryKeys.merchants(), (old) =>
          old.map((user) =>
            user.id === userId ? { ...user, accountLocked: true } : user,
          ),
        );
      }

      // Optimistically update single user detail
      if (previousUserDetail) {
        queryClient.setQueryData(profileQueryKeys.userDetail(userId), {
          ...previousUserDetail,
          accountLocked: true,
        });
      }

      // Return rollback snapshot
      return { previousUsers, previousMerchants, previousUserDetail };
    },

    // Rollback if mutation fails
    onError: (error, userId, context) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(
          profileQueryKeys.users(),
          context.previousUsers,
        );
      }
      if (context?.previousMerchants) {
        queryClient.setQueryData(
          profileQueryKeys.merchants(),
          context.previousMerchants,
        );
      }
      if (context?.previousUserDetail) {
        queryClient.setQueryData(
          profileQueryKeys.userDetail(userId),
          context.previousUserDetail,
        );
      }
    },

    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.users(),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },

    onSettled: (data, error, userId) => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.users(),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },

    retry: false,
  });
};

export const useUnblockUserPinMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId) => profileService.unblockUserPin(userId),
    mutationKey: profileQueryKeys.unblockUserPin(),

    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.users(),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },

    onSettled: (data, error, userId) => {
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.userDetail(userId),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.users(),
      });
      queryClient.invalidateQueries({
        queryKey: profileQueryKeys.merchants(),
      });
    },

    retry: false,
  });
};
